import { round2 } from './money';

export type BidPhaseDefinition = {
  id: number;
  name: string;
  sortOrder: number;
};

export type BidPhaseRollup = {
  material: number;
  labor: number;
  cost: number;
  items: number;
  priced: number;
};

export type BidBreakdownLine = {
  itemId: string;
  phaseId?: string;
  phaseName?: string;
  subPhaseName?: string | null;
  kind: 'material' | 'labor';
  qty: number;
  baseQty: number | null;
  qtyOverride: boolean;
  unitPrice: number;
  supplierPrice: number | null;
  priceOverride: boolean;
  extended: number;
  source: string;
  payableTaskGroup: string | null;
};

export type BidMaterialDetailScope =
  | { kind: 'phase'; phaseId: string; phaseName: string; costKind: 'material' | 'labor' }
  | { kind: 'total' };

export type BidUnpricedLine = {
  modelId: number;
  modelName: string;
  itemId: string;
  phaseId?: string;
  phaseName?: string;
  qty: number | null;
  baseQty: number | null;
  qtyOverride: boolean;
  payableTaskGroup: string | null;
  reason: string;
  source: string;
  lineId: number | null;
};

export type BidTotals = {
  material: number;
  bid: number;
  priced: number;
  items: number;
  phaseCosts?: Record<string, number>;
  uniqueItemCount?: number;
  uniqueUnpricedItemCount?: number;
};

export type BidModelRow = {
  id: number;
  name: string;
  lineCount: number;
  totalPriced: number;
  materialLines?: BidBreakdownLine[];
  laborLines?: BidBreakdownLine[];
  otherCost?: number;
  phases: Record<string, BidPhaseRollup>;
  salesTax: number;
  overrides?: Record<string, number | null> & { lockedPrice?: number | null };
  materialCost: number;
  bidPrice: number;
};

export type BidPayload = {
  phaseDefinitions?: BidPhaseDefinition[];
  margins: Record<string, number>;
  salesTaxPct: number;
  models: BidModelRow[];
  totals?: BidTotals;
  unpricedLines?: BidUnpricedLine[];
};

/** Community default margins — keys like `1:mat`, `1:lab`, plus salesTaxPct */
export type LiveMargins = Record<string, number> & { salesTaxPct: number };

/** Per-model live overrides — margin keys `phaseId:mat` / `phaseId:lab`, plus lockedPrice */
export type LiveModelOverrides = Record<
  number,
  Record<string, string> & { lockedPrice?: string }
>;

export type ModelBidSummary = {
  model: BidModelRow;
  materialTotal: number;
  laborTotal: number;
  materialBidTotal: number;
  laborBidTotal: number;
  totalCost: number;
  totalBid: number;
  profit: number;
  gpPct: string;
  hasLocked: boolean;
  effMaterialMargins: Record<string, number>;
  effLaborMargins: Record<string, number>;
  phaseMaterial: Record<string, number>;
  phaseLabor: Record<string, number>;
  phaseMaterialBid: Record<string, number>;
  phaseLaborBid: Record<string, number>;
};

export type BidTableRowKind =
  | 'modelHeader'
  | 'materials'
  | 'materialsMargin'
  | 'materialsBid'
  | 'labor'
  | 'laborMargin'
  | 'laborBid';

export type BidTableRow = {
  id: string;
  modelId: number;
  rowKind: BidTableRowKind;
  label: string;
  showLock: boolean;
  summary: ModelBidSummary;
  phaseAmounts: Record<string, number>;
};

export function marginMatKey(phaseId: string | number): string {
  return `${phaseId}:mat`;
}

export function marginLabKey(phaseId: string | number): string {
  return `${phaseId}:lab`;
}

/** Bid = cost + rounded margin dollars — matches Total Bid accumulation (not cost × (1+m)). */
export function phaseBidAmount(cost: number, marginPct: number): number {
  return round2(cost + round2((cost * marginPct) / 100));
}

/**
 * Nudge the last line's Total Cost / Total Price so column sums match phase (bid-table) totals.
 * Per-line tax/markup otherwise drifts from markup-on-phase-total.
 */
export function alignLineMoneyTotals<T extends { totalCost: number; totalPrice: number; profit: number; gpPct: string }>(
  lines: T[],
  phaseTotalCost: number,
  phaseTotalPrice: number
): T[] {
  if (lines.length === 0) return lines;
  const out = lines.map((l) => ({ ...l }));
  const sumCost = round2(out.reduce((s, l) => s + l.totalCost, 0));
  const sumPrice = round2(out.reduce((s, l) => s + l.totalPrice, 0));
  const last = out[out.length - 1]!;
  last.totalCost = round2(last.totalCost + round2(phaseTotalCost - sumCost));
  last.totalPrice = round2(last.totalPrice + round2(phaseTotalPrice - sumPrice));
  last.profit = round2(last.totalPrice - last.totalCost);
  last.gpPct =
    last.totalPrice > 0 ? `${((last.profit / last.totalPrice) * 100).toFixed(1)}%` : '—';
  return out;
}

/** Apply sales tax to a pre-tax material cost (labor is never taxed). */
export function costAfterSalesTax(cost: number, salesTaxPct: number): number {
  if (!(salesTaxPct > 0) || !(cost > 0)) return round2(cost);
  const tax = round2((cost * salesTaxPct) / 100);
  return round2(cost + tax);
}

export function marginPctFromBid(cost: number, bid: number): number {
  if (cost <= 0) return 0;
  return Math.round(((bid / cost - 1) * 100 + Number.EPSILON) * 10000) / 10000;
}

/** Derive margin % from an edited phase bid amount (cost is fixed). */
export function applyPhaseBidEdit(opts: {
  summary: ModelBidSummary;
  phaseKey: string;
  kind: 'material' | 'labor';
  newBid: number;
}): {
  modelId: number;
  marginKey: string;
  marginValue: string;
} {
  const { summary, phaseKey, kind, newBid } = opts;
  // phaseMaterial is after-tax; phaseLabor is untaxed.
  const cost =
    kind === 'material' ? (summary.phaseMaterial[phaseKey] ?? 0) : (summary.phaseLabor[phaseKey] ?? 0);
  const marginKey = kind === 'material' ? marginMatKey(phaseKey) : marginLabKey(phaseKey);
  const newMarginPct = marginPctFromBid(cost, round2(newBid));

  return {
    modelId: summary.model.id,
    marginKey,
    marginValue: String(newMarginPct),
  };
}

function resolveMarginPct(
  phaseId: string,
  kind: 'mat' | 'lab',
  live: LiveMargins,
  bidMargins: Record<string, number>,
  ov: Record<string, number | null | undefined>,
  liveOv: Record<string, string> | undefined
): number {
  const key = kind === 'mat' ? marginMatKey(phaseId) : marginLabKey(phaseId);
  const legacy = liveOv?.[phaseId];
  const liveVal = liveOv?.[key];
  const communityDefault =
    live[key] ?? bidMargins[key] ?? live[phaseId] ?? bidMargins[phaseId] ?? 0;

  if (liveVal !== undefined) {
    return liveVal !== '' ? Number.parseFloat(liveVal) : communityDefault;
  }
  if (legacy !== undefined && legacy !== '') {
    return Number.parseFloat(legacy);
  }
  if (ov[key] != null) return Number(ov[key]);
  if (ov[phaseId] != null) return Number(ov[phaseId] as number);
  return communityDefault;
}

function buildModelSummary(
  m: BidModelRow,
  phaseDefs: BidPhaseDefinition[],
  live: LiveMargins,
  bidMargins: Record<string, number>,
  overrides: LiveModelOverrides
): ModelBidSummary {
  const phaseMaterial: Record<string, number> = {};
  const phaseLabor: Record<string, number> = {};
  const phaseMaterialBid: Record<string, number> = {};
  const phaseLaborBid: Record<string, number> = {};

  const ov = m.overrides || {};
  const liveOv = overrides[m.id] || {};
  const salesTaxPct = live.salesTaxPct;

  const effMaterialMargins: Record<string, number> = {};
  const effLaborMargins: Record<string, number> = {};

  for (const def of phaseDefs) {
    const key = String(def.id);
    const materialPreTax = m.phases[key]?.material ?? 0;
    // Materials cost/bid use after-tax amounts (markup applies to cost after tax).
    phaseMaterial[key] = costAfterSalesTax(materialPreTax, salesTaxPct);
    phaseLabor[key] = m.phases[key]?.labor ?? 0;
    effMaterialMargins[key] = resolveMarginPct(key, 'mat', live, bidMargins, ov, liveOv);
    effLaborMargins[key] = resolveMarginPct(key, 'lab', live, bidMargins, ov, liveOv);
    phaseMaterialBid[key] = phaseBidAmount(phaseMaterial[key]!, effMaterialMargins[key]!);
    phaseLaborBid[key] = phaseBidAmount(phaseLabor[key]!, effLaborMargins[key]!);
  }

  const materialTotal = round2(Object.values(phaseMaterial).reduce((s, v) => s + v, 0));
  const laborTotal = round2(Object.values(phaseLabor).reduce((s, v) => s + v, 0));
  const totalCost = round2(materialTotal + laborTotal);

  let materialMarginDollars = 0;
  let laborMarginDollars = 0;
  for (const def of phaseDefs) {
    const key = String(def.id);
    materialMarginDollars += round2((phaseMaterial[key]! * effMaterialMargins[key]!) / 100);
    laborMarginDollars += round2((phaseLabor[key]! * effLaborMargins[key]!) / 100);
  }
  const materialBidTotal = round2(materialTotal + materialMarginDollars);
  const laborBidTotal = round2(laborTotal + laborMarginDollars);

  const savedLocked =
    ov.lockedPrice != null && Number(ov.lockedPrice) > 0 ? String(ov.lockedPrice) : '';
  const liveLockedVal = liveOv.lockedPrice;
  const lockedRaw =
    liveLockedVal !== undefined ? liveLockedVal : savedLocked !== '' ? savedLocked : '';
  const lockedNum = lockedRaw !== '' ? Number.parseFloat(lockedRaw) : Number.NaN;
  const hasLocked = !Number.isNaN(lockedNum) && lockedNum > 0;

  let computedBid = totalCost;
  if (!hasLocked) {
    computedBid = round2(totalCost + materialMarginDollars + laborMarginDollars);
  }

  const totalBid = hasLocked ? lockedNum : round2(computedBid);
  const profit = round2(totalBid - totalCost);

  return {
    model: m,
    materialTotal,
    laborTotal,
    materialBidTotal,
    laborBidTotal,
    totalCost,
    totalBid: round2(totalBid),
    profit,
    gpPct: totalBid > 0 ? ((profit / totalBid) * 100).toFixed(1) : '—',
    hasLocked,
    effMaterialMargins,
    effLaborMargins,
    phaseMaterial,
    phaseLabor,
    phaseMaterialBid,
    phaseLaborBid,
  };
}

export function computeBidTable(
  bid: BidPayload,
  live: LiveMargins,
  overrides: LiveModelOverrides
): {
  rows: BidTableRow[];
  summaries: ModelBidSummary[];
  grandTotalCost: number;
  grandBid: number;
  avgCost: number;
  avgBid: number;
  uniqueUnpricedItemCount: number;
  uniqueItemCount: number;
  unpricedLines: BidUnpricedLine[];
  pricingComplete: boolean;
} {
  let grandTotalCost = 0;
  let grandBid = 0;
  const uniqueUnpricedItemCount = bid.totals?.uniqueUnpricedItemCount ?? 0;
  const uniqueItemCount = bid.totals?.uniqueItemCount ?? 0;
  const unpricedLines = bid.unpricedLines ?? [];
  const pricingComplete = uniqueUnpricedItemCount === 0;
  const phaseDefs = bid.phaseDefinitions ?? [];

  const summaries: ModelBidSummary[] = bid.models.map((m) =>
    buildModelSummary(m, phaseDefs, live, bid.margins, overrides)
  );

  const rows: BidTableRow[] = [];
  for (const summary of summaries) {
    const mid = summary.model.id;
    const phaseBidAmounts: Record<string, number> = {};
    for (const def of phaseDefs) {
      const key = String(def.id);
      phaseBidAmounts[key] = round2(
        (summary.phaseMaterialBid[key] ?? 0) + (summary.phaseLaborBid[key] ?? 0)
      );
    }
    rows.push({
      id: `${mid}-header`,
      modelId: mid,
      rowKind: 'modelHeader',
      label: summary.model.name,
      showLock: true,
      summary,
      phaseAmounts: phaseBidAmounts,
    });
    rows.push({
      id: `${mid}-materials`,
      modelId: mid,
      rowKind: 'materials',
      label: 'Materials',
      showLock: false,
      summary,
      phaseAmounts: summary.phaseMaterial,
    });
    rows.push({
      id: `${mid}-materials-margin`,
      modelId: mid,
      rowKind: 'materialsMargin',
      label: 'Materials Margin',
      showLock: false,
      summary,
      phaseAmounts: {},
    });
    rows.push({
      id: `${mid}-materials-bid`,
      modelId: mid,
      rowKind: 'materialsBid',
      label: 'Materials Bid',
      showLock: false,
      summary,
      phaseAmounts: summary.phaseMaterialBid,
    });
    rows.push({
      id: `${mid}-labor`,
      modelId: mid,
      rowKind: 'labor',
      label: 'Labor',
      showLock: false,
      summary,
      phaseAmounts: summary.phaseLabor,
    });
    rows.push({
      id: `${mid}-labor-margin`,
      modelId: mid,
      rowKind: 'laborMargin',
      label: 'Labor Margin',
      showLock: false,
      summary,
      phaseAmounts: {},
    });
    rows.push({
      id: `${mid}-labor-bid`,
      modelId: mid,
      rowKind: 'laborBid',
      label: 'Labor Bid',
      showLock: false,
      summary,
      phaseAmounts: summary.phaseLaborBid,
    });
    grandTotalCost += summary.totalCost;
    grandBid += summary.totalBid;
  }

  const modelCount = summaries.length;
  const roundedGrandBid = round2(grandBid);
  return {
    rows,
    summaries,
    grandTotalCost,
    grandBid: roundedGrandBid,
    avgCost: modelCount ? round2(grandTotalCost / modelCount) : 0,
    avgBid: modelCount ? round2(roundedGrandBid / modelCount) : 0,
    uniqueUnpricedItemCount,
    uniqueItemCount,
    unpricedLines,
    pricingComplete,
  };
}

/** @deprecated Use computeBidTable */
export type ComputedBidRow = ModelBidSummary;

/** @deprecated Use computeBidTable */
export function computeBidRows(bid: BidPayload, live: LiveMargins, overrides: LiveModelOverrides) {
  const result = computeBidTable(bid, live, overrides);
  return {
    rows: result.summaries,
    grandTotalCost: result.grandTotalCost,
    grandBid: result.grandBid,
    uniqueUnpricedItemCount: result.uniqueUnpricedItemCount,
    uniqueItemCount: result.uniqueItemCount,
    unpricedLines: result.unpricedLines,
    pricingComplete: result.pricingComplete,
  };
}
